/**
 * Binder assembly: build an accurate table of contents from multiple tools
 * and assemble the final printable binder document.
 *
 * Section descriptor shape: { name: string, hasContent: boolean, getContent: () => string }
 */

/**
 * Build the ordered list of section names that have content (for TOC).
 * @param {Array<{ name: string, hasContent: boolean }>} sectionDescriptors
 * @returns {Array<{ name: string, index: number }>} Sections to include in TOC (1-based index)
 */
export function buildTableOfContents(sectionDescriptors) {
  const withContent = sectionDescriptors.filter((s) => s.hasContent);
  return withContent.map((s, i) => ({ name: s.name, index: i + 1 }));
}

/**
 * Assemble the binder body HTML: cover, TOC, then for each section with content a divider + content.
 * @param {Array<{ name: string, hasContent: boolean, getContent: () => string }>} sectionDescriptors
 * @param {{ title?: string, subtitle?: string, preparedBy?: string }} coverOptions
 * @returns {string} HTML string for the binder body (no document wrapper)
 */
export function assembleBinderBody(sectionDescriptors, coverOptions = {}) {
  const today = new Date().toLocaleDateString();
  const title = coverOptions.title ?? 'Emergency Preparedness Plan';
  const subtitle = coverOptions.subtitle ?? 'Family Emergency Preparedness Binder';
  const preparedBy = coverOptions.preparedBy ?? '[Your Name]';

  const sections = sectionDescriptors.filter((s) => s.hasContent);
  let content = '';

  // Cover Page
  content += `
      <div class="cover-page">
        <div class="cover-page-inner">
          <div class="cover-title">${title}</div>
          <div class="cover-subtitle">${subtitle}</div>
          <div class="cover-date">
            <div>Last Updated: ${today}</div>
            <div style="margin-top: 20px;">Prepared by: ${preparedBy}</div>
          </div>
        </div>
      </div>
    `;

  // Table of Contents
  const tocEntries = buildTableOfContents(sectionDescriptors);
  content += `
      <div class="toc-page">
        <div class="toc-title">Table of Contents</div>
        ${tocEntries.map((entry) => `<div class="toc-item">${entry.index}. ${entry.name}</div>`).join('')}
      </div>
    `;

  // Each section: divider + content
  sections.forEach((section) => {
    content += `<div class="divider-page"><div class="divider-page-inner"><div class="divider-title">${section.name}</div></div></div>`;
    content += `<div class="section-page">`;
    content += typeof section.getContent === 'function' ? section.getContent() : '';
    content += `</div>`;
  });

  return content;
}

/**
 * Wrap binder body in full HTML document (styles, scripts for maps/diagrams).
 * @param {string} binderContent - HTML string from assembleBinderBody
 * @param {{ title?: string }} options
 * @returns {string} Full HTML document
 */
export function wrapInHtmlDocument(binderContent, options = {}) {
  const pageTitle = options.title ?? 'Emergency Preparedness Plan - Complete Binder';
  return getBinderDocumentTemplate().replace('__BINDER_CONTENT__', binderContent).replace('__PAGE_TITLE__', pageTitle);
}

/**
 * Full HTML document template for the binder (placeholder: __BINDER_CONTENT__, __PAGE_TITLE__).
 * Shared so all tools produce a consistent printable binder.
 */
function getBinderDocumentTemplate() {
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>__PAGE_TITLE__</title>
  <link rel="stylesheet" href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css" 
    integrity="sha256-p4NxAoJBhIIN+hmNHrzRCf9tD/miZyoHS5obTRR9BMY=" 
    crossorigin=""/>
  <script src="https://cdn.jsdelivr.net/npm/mermaid@10/dist/mermaid.min.js"></script>
  <style>
    @page { size: letter; margin: 0.75in; width: 8.5in; height: 11in; }
    * { box-sizing: border-box; }
    html, body { width: 100%; margin: 0; padding: 0; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif; font-size: 11pt; line-height: 1.7; color: #1f2937; background: #ffffff; }
    .page-wrapper { max-width: 7in; width: 7in; margin: 0 auto; padding: 0; }
    .cover-page { text-align: center; padding: 2in 0.75in; page-break-after: always; page-break-inside: avoid; background: white; color: #1f2937; }
    .cover-page-inner { display: block; }
    .cover-title { font-size: 36pt; font-weight: 700; margin-bottom: 20px; letter-spacing: -1px; color: #7c3aed; }
    .cover-subtitle { font-size: 18pt; margin-bottom: 40px; font-weight: 500; color: #4b5563; }
    .cover-date { font-size: 12pt; margin-top: 2in; color: #6b7280; font-weight: 500; }
    .toc-page { page-break-before: always; page-break-after: always; page-break-inside: avoid; padding: 20px 0; min-height: 9.5in; }
    .toc-title { font-size: 28pt; font-weight: 700; margin-bottom: 35px; color: #7c3aed; padding-bottom: 15px; border-bottom: 4px solid #7c3aed; }
    .toc-item { margin-bottom: 18px; font-size: 14pt; padding: 12px 15px; background: #f9fafb; border-radius: 6px; border-left: 4px solid #7c3aed; font-weight: 500; }
    .divider-page { text-align: center; page-break-before: always; page-break-after: always; page-break-inside: avoid; padding: 2in 0.75in; background: white; }
    .divider-page-inner { display: block; }
    .divider-title { font-size: 28pt; font-weight: 700; border-bottom: 4px solid #7c3aed; padding-bottom: 15px; display: inline-block; color: #7c3aed; margin: 0 auto; }
    .section-page { page-break-before: always; page-break-after: auto; }
    .print-header { background: linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%); color: white; padding: 20px 25px; margin-bottom: 25px; border-radius: 8px; box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1); }
    .print-title { font-size: 24pt; font-weight: 700; margin-bottom: 6px; letter-spacing: -0.5px; }
    .print-date { font-size: 10pt; color: rgba(255, 255, 255, 0.9); font-weight: 500; }
    .section-content { margin-bottom: 25px; }
    .section-title { font-size: 16pt; font-weight: 700; margin-top: 25px; margin-bottom: 12px; color: #7c3aed; padding-bottom: 8px; border-bottom: 2px solid #7c3aed; }
    .subsection-title { font-size: 13pt; font-weight: 600; margin-top: 20px; margin-bottom: 10px; color: #4b5563; padding-left: 12px; border-left: 3px solid #7c3aed; }
    .item-row { margin-bottom: 10px; padding: 10px 12px; background: #f9fafb; border-radius: 6px; border-left: 3px solid #e5e7eb; }
    .label { font-weight: 600; display: inline-block; min-width: 150px; color: #374151; margin-right: 8px; }
    .value { color: #1f2937; }
    .highlight-box { background: #fef3c7; border: 2px solid #f59e0b; border-radius: 6px; padding: 12px; margin: 15px 0; }
    .info-card { background: white; border: 1px solid #e5e7eb; border-radius: 6px; padding: 15px; margin: 12px 0; }
    @media print {
      @page { size: letter; margin: 0.75in; }
      * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
      html, body { width: 8.5in; height: 11in; margin: 0; padding: 0; }
      .no-print { display: none !important; }
      .page-break { page-break-after: always; }
      .cover-page { page-break-after: always !important; page-break-inside: avoid !important; background: white !important; width: 7in; min-height: 9.5in; }
      .toc-page { page-break-before: always !important; page-break-after: always !important; page-break-inside: avoid !important; width: 7in; min-height: 9.5in; }
      .divider-page { page-break-before: always !important; page-break-after: always !important; page-break-inside: avoid !important; background: white !important; width: 7in; min-height: 9.5in; }
      .section-page { page-break-before: always !important; width: 7in; }
      .print-header, .item-row, .highlight-box { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      .item-row, .highlight-box, .info-card { page-break-inside: avoid; }
      .map-container-print { width: 100% !important; min-width: 100% !important; max-width: 100% !important; height: 500px !important; display: block !important; position: relative !important; overflow: hidden !important; box-sizing: border-box !important; }
      .map-container-print .leaflet-container { width: 100% !important; height: 100% !important; min-width: 100% !important; max-width: 100% !important; }
      .page-wrapper { max-width: 7in !important; width: 7in !important; margin: 0 auto !important; padding: 0 !important; }
    }
  </style>
</head>
<body>
  <div class="page-wrapper">
__BINDER_CONTENT__
  </div>
  <script src="https://unpkg.com/leaflet@1.9.4/dist/leaflet.js" integrity="sha256-20nQCchB9co0qIjJZRGuk2/Z9VM+kNiyxNV1lvTlZBo=" crossorigin=""></script>
  <script>
    if (typeof mermaid !== 'undefined') {
      mermaid.initialize({ startOnLoad: true, theme: 'default', securityLevel: 'loose', flowchart: { useMaxWidth: true, htmlLabels: true, curve: 'basis', nodeSpacing: 80, rankSpacing: 100 } });
    }
    window.addEventListener('load', function() {
      setTimeout(function() {
        if (typeof L === 'undefined') return;
        document.querySelectorAll('.map-container-print').forEach(function(container) {
          const mapDataStr = container.getAttribute('data-map-data');
          if (mapDataStr) {
            try {
              const mapDataJson = mapDataStr.replace(/&quot;/g, '"').replace(/&#39;/g, "'").replace(/&amp;/g, '&').replace(/&lt;/g, '<').replace(/&gt;/g, '>');
              const data = JSON.parse(mapDataJson);
              const map = L.map(container, { zoomControl: true, dragging: false, touchZoom: false, doubleClickZoom: false, scrollWheelZoom: false, boxZoom: false, keyboard: false });
              const containerWidth = container.offsetWidth || container.parentElement.offsetWidth || 800;
              container.style.width = containerWidth + 'px';
              container.style.height = '500px';
              container.style.display = 'block';
              container.style.position = 'relative';
              container.style.overflow = 'hidden';
              L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', { attribution: '&copy; OpenStreetMap contributors', maxZoom: 19 }).addTo(map);
              const savedCenter = data.center || [39.8283, -98.5795];
              const savedZoom = data.zoom || 13;
              map.setView(savedCenter, savedZoom);
              setTimeout(function() { map.invalidateSize(); if (window.L && map) { map.getContainer().style.width = containerWidth + 'px'; map.invalidateSize(); } }, 100);
              var bounds = null, hasContent = false;
              if (data.markers && data.markers.length > 0) { bounds = L.latLngBounds(data.markers.map(function(m) { return m.position; })); hasContent = true; }
              if (data.polylines && data.polylines.length > 0) { data.polylines.forEach(function(p) { if (p.positions && p.positions.length > 0) { if (!bounds) bounds = L.latLngBounds(p.positions); else bounds.extend(p.positions); hasContent = true; } }); }
              if (data.texts && data.texts.length > 0) { data.texts.forEach(function(t) { if (!bounds) bounds = L.latLngBounds([t.position]); else bounds.extend(t.position); hasContent = true; }); }
              if (hasContent && bounds && bounds.isValid()) { var centerPoint = L.latLng(savedCenter[0], savedCenter[1]); if (!bounds.contains(centerPoint)) { bounds.extend(savedCenter); map.fitBounds(bounds, { padding: [50, 50] }); } }
              if (data.markers) data.markers.forEach(function(marker) { var color = marker.color || data.markerColor || '#3388ff'; var svgIcon = '<svg width="25" height="41" viewBox="0 0 25 41" xmlns="http://www.w3.org/2000/svg"><path fill="' + color + '" stroke="#fff" stroke-width="1.5" d="M12.5 0C5.6 0 0 5.6 0 12.5c0 8.7 12.5 28.5 12.5 28.5S25 21.2 25 12.5C25 5.6 19.4 0 12.5 0z"/><circle fill="#fff" cx="12.5" cy="12.5" r="4"/></svg>'; L.marker(marker.position, { icon: L.divIcon({ html: svgIcon, className: 'custom-marker-icon', iconSize: [25, 41], iconAnchor: [12, 41] }) }).addTo(map); });
              if (data.polylines) data.polylines.forEach(function(p) { L.polyline(p.positions, { color: p.color || data.color || '#FF0000', weight: 5, opacity: 0.7 }).addTo(map); });
              if (data.texts) data.texts.forEach(function(t) { var color = t.color || data.textColor || '#000000'; var text = (t.text || 'Text').replace(/</g, '&lt;').replace(/>/g, '&gt;'); L.marker(t.position, { icon: L.divIcon({ html: '<div style="background-color: rgba(255,255,255,0.9); border: 2px solid ' + color + '; border-radius: 4px; padding: 4px 8px; color: ' + color + '; font-weight: bold; font-size: 14px;">' + text + '</div>', className: 'custom-text-icon', iconSize: [null, null], iconAnchor: [0, 0] }) }).addTo(map); });
            } catch (e) { console.error('Error initializing map:', e); container.innerHTML = '<p style="padding: 20px; color: #666;">Error loading map. Please view in the application.</p>'; }
          }
        });
      }, 500);
    });
  </script>
</body>
</html>`;
}
